
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label"; 
import { Investor } from "@/types/investor";
import { Loader2 } from "lucide-react";

interface BatchProcessorProps {
  investors: Investor[];
  processedCount: number;
  isProcessing: boolean;
  onProcessBatch: (batchSize: number) => Promise<void>;
  isInvestorProcessed?: (investor: Investor) => boolean;
  pendingSaveCount?: number;
}

const BatchProcessor = ({
  investors,
  processedCount,
  isProcessing,
  onProcessBatch,
  isInvestorProcessed,
  pendingSaveCount = 0
}: BatchProcessorProps) => {
  const [batchSize, setBatchSize] = useState(50);
  const [displayedProcessedCount, setDisplayedProcessedCount] = useState(processedCount);
  
  // Update displayed count whenever processedCount changes
  useEffect(() => {
    setDisplayedProcessedCount(processedCount);
  }, [processedCount]);
  
  // Live update of actually processed investors using the provided function
  useEffect(() => {
    if (isProcessing && isInvestorProcessed) {
      // During processing, check every second for updates
      const interval = setInterval(() => {
        const actualProcessedCount = investors.filter(isInvestorProcessed).length;
        setDisplayedProcessedCount(actualProcessedCount);
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [isProcessing, investors, isInvestorProcessed]);
  
  // Count actually processed investors using the provided function
  let actualProcessedCount = processedCount;
  if (isInvestorProcessed && !isProcessing) {
    actualProcessedCount = investors.filter(isInvestorProcessed).length;
  }
  
  // Calculate remaining investors
  const remainingCount = investors.length - displayedProcessedCount;
  
  // Compute suggested batch size based on remaining count
  const suggestedBatchSize = Math.min(100, Math.max(10, remainingCount));
  
  // Handle batch size change
  const handleBatchSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setBatchSize(isNaN(value) ? 10 : Math.max(1, value));
  };
  
  // Handle batch processing
  const handleProcessBatch = () => {
    // Use a smaller batch size if remaining count is smaller than selected batch size
    const effectiveBatchSize = Math.min(batchSize, remainingCount);
    onProcessBatch(effectiveBatchSize);
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-end mb-1">
        <Label htmlFor="batchSize">Batch Size</Label>
        <div className="text-sm text-muted-foreground">
          {displayedProcessedCount} of {investors.length} processed
          {pendingSaveCount > 0 && (
            <span className="ml-2 text-amber-500">
              ({pendingSaveCount} updates pending)
            </span>
          )}
        </div>
      </div>
      
      <div className="flex gap-3">
        <Input
          id="batchSize"
          type="number"
          value={batchSize}
          onChange={handleBatchSizeChange}
          className="w-20"
          min={1}
          max={500}
        />
        
        <div className="grid grid-cols-3 gap-2 flex-1">
          <Button 
            variant="outline" 
            onClick={() => setBatchSize(10)}
            className="px-2.5"
          >
            10
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setBatchSize(50)}
            className="px-2.5"
          >
            50
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setBatchSize(suggestedBatchSize)}
            className="px-2.5"
          >
            {suggestedBatchSize}
          </Button>
        </div>
        
        <Button 
          onClick={handleProcessBatch} 
          disabled={isProcessing || remainingCount <= 0}
          className="flex-1"
          variant={pendingSaveCount > 0 ? "outline" : "default"}
        >
          {isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : remainingCount <= 0 ? (
            "All Processed"
          ) : (
            `Process ${Math.min(batchSize, remainingCount)}`
          )}
        </Button>
      </div>
      
      <div className="text-xs text-muted-foreground mt-1">
        {isProcessing ? (
          <span>Processing batch, please wait... ({displayedProcessedCount} of {investors.length} processed)</span>
        ) : remainingCount > 0 ? (
          <span>{remainingCount} investors remaining to process</span>
        ) : (
          <span>All investors have been processed</span>
        )}
        {pendingSaveCount > 0 && (
          <span className="block text-amber-500 mt-1">
            {pendingSaveCount} updates waiting to be saved to the database
          </span>
        )}
      </div>
    </div>
  );
};

export default BatchProcessor;
